import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { socketService } from '../services/socketService';
import { useGameStore } from '../store/gameStore';

interface GameData {
  playerSequence: string[];
  displayOrder: string[];
  currentDealer: string;
  currentTurn: string;
  discardPile: Card[];
  phase: string;
  playerName?: string;
  playerCards?: Card[];
  gameState?: {
    phase: string;
    leaderName: string;
    currentDealer: string;
    playerSequence: string[];
    displayOrder: string[];
    currentTurn: string;
    deckColors: string[];
  };
}

interface Card {
  value: string;
  suit: string;
  faceUp: boolean;
  color: string;
}

interface DragEvent extends React.DragEvent<HTMLDivElement> {
  dataTransfer: DataTransfer;
}

// Card component for displaying actual card images
const CardImage: React.FC<{ card: Card; width?: number; height?: number }> = ({ 
  card, 
  width = 146,
  height = 198
}) => {
  const getCardPosition = (card: Card) => {
    if (!card.faceUp) {
      return null;
    }
    
    // modern.png is 1898×792 pixels with 13 columns and 4 rows
    // Each card is 146×198 pixels
    const valueOrder = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
    const suitOrder = ['♠', '♥', '♣', '♦'];
    
    const valueIndex = valueOrder.indexOf(card.value);
    const suitIndex = suitOrder.indexOf(card.suit);
    
    if (valueIndex === -1 || suitIndex === -1) {
      console.error('Invalid card:', card);
      return null;
    }

    // Calculate position based on card dimensions
    const x = valueIndex * 146; // Each card is 146px wide
    const y = suitIndex * 198; // Each card is 198px high
    
    return { x, y };
  };

  if (!card.faceUp) {
    const backImage = card.color === 'red' 
      ? '/images/cards/rhombus_red.png' 
      : '/images/cards/rhombus_blue.png';
    
    return (
      <CardImageContainer width={width} height={height}>
        <img 
          src={backImage}
          alt="Card back"
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'contain',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
          }}
        />
      </CardImageContainer>
    );
  }

  const position = getCardPosition(card);
  if (!position) return null;

  return (
    <CardImageContainer width={width} height={height}>
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundImage: 'url(/images/cards/modern.png)',
          backgroundPosition: `-${position.x}px -${position.y}px`,
          backgroundSize: '1898px 792px', // Full sprite sheet size
          backgroundRepeat: 'no-repeat',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          backgroundColor: 'white',
          imageRendering: 'pixelated',
          transform: 'scale(1)',
          transformOrigin: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
        title={`${card.value} of ${card.suit}`}
      />
    </CardImageContainer>
  );
};

const CardImageContainer = styled.div<{ width: number; height: number }>`
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  border-radius: 12px;
  overflow: hidden;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 2px;
  aspect-ratio: 146/198;
`;

const GameScreen: React.FC = () => {
  const { gameRole } = useGameStore();
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [playerCards, setPlayerCards] = useState<Card[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [playerName, setPlayerName] = useState<string>('');
  const [draggedCard, setDraggedCard] = useState<number | null>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const [moveOffsets, setMoveOffsets] = useState<Map<number, number>>(new Map());
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const dragStartPosRef = useRef<{ x: number; y: number; cardRect: DOMRect } | null>(null);
  const cardElementsRef = useRef<{ [key: number]: DOMRect }>({});
  const [targetIndex, setTargetIndex] = useState<number | null>(null);
  const [movingCards, setMovingCards] = useState<Map<number, number>>(new Map());
  const [deckColorPattern, setDeckColorPattern] = useState<string[]>([]);

  // Generate deck color pattern once when gameData changes
  useEffect(() => {
    if (gameData?.gameState?.deckColors && gameData.gameState.deckColors.length > 1) {
      // Generate a fixed pattern for two decks
      const pattern = [...Array(12)].map(() => {
        return Math.random() < 0.5 ? gameData.gameState!.deckColors[0] : gameData.gameState!.deckColors[1];
      });
      setDeckColorPattern(pattern);
    } else {
      setDeckColorPattern([]);
    }
  }, [gameData?.gameState?.deckColors]);

  const getCardShift = (index: number) => {
    if (!draggedCard || targetIndex === null) return null;
    if (index === draggedCard) return null;

    if (targetIndex > draggedCard) {
      // Moving right
      if (index > draggedCard && index <= targetIndex) return 'left';
    } else {
      // Moving left
      if (index >= targetIndex && index < draggedCard) return 'right';
    }
    return null;
  };

  useEffect(() => {
    const socket = socketService.connect();
    let timeoutId: NodeJS.Timeout;

    const handleGameStarted = (data: GameData) => {
      console.log('Game started data:', data);
      console.log('Game started playerName:', data.playerName);
      console.log('Game started currentDealer:', data.currentDealer);
      console.log('Game started currentTurn:', data.currentTurn);
      setGameData(data);
      
      // Set player name if available - this should be the primary source
      if (data.playerName && data.playerName !== 'Unknown Player') {
        console.log('Setting player name from gameStarted:', data.playerName);
        setPlayerName(data.playerName);
      } else {
        console.log('No valid playerName in gameStarted data');
      }
      
      setLoading(false);
    };

    const handlePlayerCards = (data: { cards: Card[] }) => {
      console.log('Received player cards:', data);
      setPlayerCards(data.cards);
    };

    // Get initial game state when component mounts
    const initializeGame = async () => {
      // Prevent multiple initializations
      if (!loading || initialized) return;
      
      setInitialized(true);
      
      try {
        console.log('Initializing game screen...');
        const response = await socketService.checkGameStatus();
        console.log('Game status response:', response);
        
        if (response.phase === 'playing') {
          console.log('Game is playing, requesting current game data...');
          // Request current game data
          console.log('About to emit getCurrentGameData...');
          
          let responseReceived = false;
          
          socket.emit('getCurrentGameData', {}, (gameDataResponse: any) => {
            console.log('getCurrentGameData callback received:', gameDataResponse);
            console.log('Response playerName:', gameDataResponse?.playerName);
            responseReceived = true;
            
            if (gameDataResponse && !gameDataResponse.error) {
              console.log('Setting game data:', gameDataResponse);
              setGameData(gameDataResponse);
              
              // Set player name from response
              if (gameDataResponse.playerName) {
                console.log('Setting player name from response:', gameDataResponse.playerName);
                setPlayerName(gameDataResponse.playerName);
              } else {
                console.log('No playerName in response, using fallback');
                setPlayerName('My Name');
              }
              
              setLoading(false);
              if (timeoutId) clearTimeout(timeoutId);
            } else {
              console.error('Error in getCurrentGameData:', gameDataResponse?.error);
              setError(gameDataResponse?.error || 'Failed to get game data');
              setLoading(false);
            }
          });

          // Remove the separate getPlayerName call since we get it from getCurrentGameData now

          console.log('getCurrentGameData emitted, waiting for response...');

          // Add timeout for getCurrentGameData
          timeoutId = setTimeout(() => {
            if (!responseReceived && loading) {
              console.log('getCurrentGameData timeout - no response received');
              setError('Timeout getting game data');
              setLoading(false);
            }
          }, 5000);
        } else {
          console.log('Game is not in playing phase:', response.phase);
          setError(`Game is not active (phase: ${response.phase})`);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to initialize game:', err);
        setError('Failed to load game');
        setLoading(false);
      }
    };

    socket.on('gameStarted', handleGameStarted);
    socket.on('playerCards', handlePlayerCards);
    socket.on('error', (error: { message: string }) => {
      console.error('Game error:', error);
      setError(error.message);
    });

    // Only initialize once
    if (loading) {
      initializeGame();
    }

    return () => {
      socket.off('gameStarted', handleGameStarted);
      socket.off('playerCards', handlePlayerCards);
      socket.off('error');
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // Empty dependency array to run only once

  useEffect(() => {
    setPlayerCards(playerCards);
  }, [playerCards]);

  const handleMouseDown = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    const card = cardRefs.current.get(index);
    if (!card) return;

    const rect = card.getBoundingClientRect();
    dragStartPosRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      cardRect: rect
    };

    setDragPosition({
      x: 0,
      y: 0
    });
    
    setDraggedCard(index);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (draggedCard === null || !dragStartPosRef.current) return;

      const { x: startX, y: startY, cardRect } = dragStartPosRef.current;
      const deltaX = e.clientX - cardRect.left - startX;
      const deltaY = e.clientY - cardRect.top - startY;

      setDragPosition({
        x: deltaX,
        y: deltaY
      });

      // Find potential swap target
      let newTarget: number | null = null;
      cardRefs.current.forEach((targetCard, index) => {
        if (index === draggedCard) return;

        const targetRect = targetCard.getBoundingClientRect();
        if (e.clientX >= targetRect.left && e.clientX <= targetRect.right) {
          newTarget = index;
        }
      });

      setTargetIndex(newTarget);
    };

    const handleMouseUp = async () => {
      if (draggedCard === null || targetIndex === null) {
        setDraggedCard(null);
        setDragPosition(null);
        setTargetIndex(null);
        return;
      }

      const newMovingCards = new Map<number, number>();
      const cardWidth = 156; // Card width + margin

      if (targetIndex > draggedCard) {
        // Moving right
        for (let i = draggedCard + 1; i <= targetIndex; i++) {
          newMovingCards.set(i, -cardWidth);
        }
      } else {
        // Moving left
        for (let i = targetIndex; i < draggedCard; i++) {
          newMovingCards.set(i, cardWidth);
        }
      }

      // Start animation
      setMovingCards(newMovingCards);

      // Wait for animation
      await new Promise(resolve => setTimeout(resolve, 300));

      // Update card positions
      const newCards = [...playerCards];
      const [movedCard] = newCards.splice(draggedCard, 1);
      newCards.splice(targetIndex, 0, movedCard);
      
      // Reset states
      setDraggedCard(null);
      setDragPosition(null);
      setTargetIndex(null);
      setMovingCards(new Map());
      
      // Update cards array
      setPlayerCards(newCards);
    };

    if (draggedCard !== null) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggedCard, targetIndex, playerCards]);

  const handleDrop = (index: number) => {
    if (draggedCard === null) return;
    
    const newCards = [...playerCards];
    const [movedCard] = newCards.splice(draggedCard, 1);
    newCards.splice(index, 0, movedCard);
    
    setPlayerCards(newCards);
    setDraggedCard(null);
  };

  const handleNextGame = async () => {
    try {
      const socket = socketService.connect();
      await new Promise<void>((resolve, reject) => {
        socket.emit('nextGame', {}, (response: any) => {
          if (response?.error) {
            reject(new Error(response.error));
          } else {
            resolve();
          }
        });
      });
      console.log('Next game started successfully');
    } catch (err) {
      console.error('Error starting next game:', err);
    }
  };

  if (loading) {
    return <LoadingContainer><LoadingSpinner /></LoadingContainer>;
  }

  if (error) {
    return <ErrorContainer>{error}</ErrorContainer>;
  }

  if (!gameData) {
    return <LoadingContainer><LoadingSpinner /></LoadingContainer>;
  }

  // Debug logging for turn data
  console.log('=== GameScreen Debug ===');
  console.log('Current gameData:', gameData);
  console.log('Current dealer:', gameData.currentDealer);
  console.log('Current turn:', gameData.currentTurn);
  console.log('Player sequence:', gameData.playerSequence);
  console.log('========================');

  const currentPlayerIndex = gameData.currentTurn ?
    gameData.playerSequence.indexOf(gameData.currentTurn) : 0;

  const faceUpCards = playerCards.filter(card => card.faceUp);
  const faceDownCards = playerCards.filter(card => !card.faceUp);

  return (
    <GameContainer>
      <MainGameArea>
        <PlayersColumn>
          {(gameData.displayOrder || gameData.playerSequence || []).map((name, index) => (
            <PlayerButton
              key={name}
              $isLeader={name === gameData.gameState?.leaderName || index === 0}
              $isDealer={name === gameData.currentDealer}
              $isCurrentPlayer={name === gameData.currentTurn}
            >
              <div className="player-info">
                {name === gameData.gameState?.leaderName && <LeaderIcon />}
                <span className="player-name">{name}</span>
              </div>
              <div className="status-badges">
                {name === gameData.currentDealer && <StatusBadge $type="dealer"></StatusBadge>}
                {name === gameData.currentTurn && <StatusBadge $type="turn"></StatusBadge>}
              </div>
            </PlayerButton>
          ))}
        </PlayersColumn>

        {/* Center area */}
        <CenterArea>
          {/* Turn indicator */}
          {gameData.currentTurn && (
            <TurnIndicator>
              {gameData.currentTurn} to start the betting
            </TurnIndicator>
          )}

          {/* Cards area */}
          <CardsArea>
                        <DeckArea>
              {/* Deck stack - mixed colors for 2-deck games */}
              <DeckStack>
                {gameData.gameState?.deckColors && gameData.gameState.deckColors.length > 0 ? (
                  gameData.gameState.deckColors.length === 1 ? (
                    // Single deck - all same color
                    [...Array(6)].map((_, stackIndex) => (
                      <StackedCard
                        key={stackIndex}
                        $stackIndex={stackIndex}
                      >
                        <CardImage
                          card={{ 
                            value: '', 
                            suit: '', 
                            faceUp: false, 
                            color: gameData.gameState?.deckColors?.[0] || 'blue'
                          }}
                          width={146}
                          height={198}
                        />
                      </StackedCard>
                    ))
                  ) : (
                    // Two decks - randomly mixed as one stack
                    deckColorPattern.map((color, stackIndex) => (
                      <StackedCard
                        key={stackIndex}
                        $stackIndex={stackIndex}
                      >
                        <CardImage
                          card={{ 
                            value: '', 
                            suit: '', 
                            faceUp: false, 
                            color
                          }}
                          width={146}
                          height={198}
                        />
                      </StackedCard>
                    ))
                  )
                ) : (
                  // Fallback for single deck
                  [...Array(6)].map((_, stackIndex) => (
                    <StackedCard
                      key={stackIndex}
                      $stackIndex={stackIndex}
                    >
                      <CardImage
                        card={{ value: '', suit: '', faceUp: false, color: 'blue' }}
                        width={146}
                        height={198}
                      />
                    </StackedCard>
                  ))
                )}
              </DeckStack>
              
              {/* Discard pile - positioned more to the side */}
              {gameData.discardPile.length > 0 && (
                <DiscardCard>
                  <CardImage
                    card={gameData.discardPile[gameData.discardPile.length - 1]}
                    width={146}
                    height={198}
                  />
                </DiscardCard>
              )}
            </DeckArea>
          </CardsArea>

          {/* Leader's buttons */}
          {gameRole === 'leader' && (
            <ButtonGroup>
              <EndPhaseButton>End Phase</EndPhaseButton>
              <NextGameButton onClick={handleNextGame}>Next Game</NextGameButton>
            </ButtonGroup>
          )}
        </CenterArea>
      </MainGameArea>

      {/* Player's hand */}
      <PlayerHandArea>
        <PlayerName>{playerName}</PlayerName>
        <CardsContainer>
          {faceUpCards.map((card, index) => (
            <CardWrapper 
              key={`up-${index}`}
              ref={el => el && cardRefs.current.set(index, el)}
              $faceUp={true}
              $isDragging={draggedCard === index}
              $dragPosition={draggedCard === index && dragPosition ? dragPosition : undefined}
              $isMoving={movingCards.has(index)}
              $movePosition={movingCards.get(index)}
              onMouseDown={(e) => handleMouseDown(index, e)}
            >
              <CardImage
                card={card}
                width={146}
                height={198}
              />
            </CardWrapper>
          ))}
          {faceDownCards.map((card, index) => {
            const actualIndex = faceUpCards.length + index;
            return (
              <CardWrapper 
                key={`down-${index}`}
                ref={el => el && cardRefs.current.set(actualIndex, el)}
                $faceUp={false}
                $isDragging={draggedCard === actualIndex}
                $dragPosition={draggedCard === actualIndex && dragPosition ? dragPosition : undefined}
                $isMoving={movingCards.has(actualIndex)}
                $movePosition={movingCards.get(actualIndex)}
                onMouseDown={(e) => handleMouseDown(actualIndex, e)}
              >
                <CardImage
                  card={card}
                  width={146}
                  height={198}
                />
              </CardWrapper>
            );
          })}
        </CardsContainer>
      </PlayerHandArea>
    </GameContainer>
  );
};

// Updated styled components
const GameContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: radial-gradient(circle at center, #2a8d2a 0%, #1a5d1a 50%, #0f3d0f 100%);
  color: white;
  padding: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const MainGameArea = styled.div`
  display: flex;
  gap: 20px;
  flex: 1;
  padding: 20px;
  position: relative;
  min-height: 70vh;
`;

const PlayersColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 280px;
  position: absolute;
  left: 30px;
  top: 50%;
  transform: translateY(-50%);
  padding: 25px 20px 20px 20px;
  background: linear-gradient(135deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.08) 100%);
  border-radius: 25px;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255,255,255,0.15);
  box-shadow: 0 15px 40px rgba(0,0,0,0.3);
  z-index: 10;

  &::before {
    content: 'PLAYERS';
    position: absolute;
    top: -15px;
    left: 25px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    padding: 6px 20px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 1.2px;
    box-shadow: 0 6px 15px rgba(102, 126, 234, 0.4);
    border: 2px solid rgba(255,255,255,0.2);
  }
`;

const LeaderIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: '12px', flexShrink: 0 }}
  >
    <defs>
      <linearGradient id="leaderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffd700" />
        <stop offset="50%" stopColor="#ffed4e" />
        <stop offset="100%" stopColor="#f39c12" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
        <feMerge> 
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <path
      d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
      fill="url(#leaderGradient)"
      stroke="rgba(255,215,0,0.8)"
      strokeWidth="1"
      filter="url(#glow)"
    />
  </svg>
);

const PlayerButton = styled.div<{
  $isLeader: boolean;
  $isDealer: boolean;
  $isCurrentPlayer: boolean;
}>`
  padding: 12px 20px;
  border-radius: 16px;
  color: white;
  background: ${props => 
    props.$isCurrentPlayer
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : props.$isLeader
        ? 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
        : 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)'
  };
  box-shadow: ${props => 
    props.$isCurrentPlayer
      ? '0 8px 25px rgba(102, 126, 234, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
      : props.$isLeader
        ? '0 6px 20px rgba(255, 215, 0, 0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
        : '0 4px 15px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)'
  };
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: ${props => 
    props.$isCurrentPlayer
      ? '2px solid rgba(102, 126, 234, 0.6)'
      : props.$isLeader
        ? '2px solid rgba(255, 215, 0, 0.3)'
        : '2px solid rgba(255,255,255,0.1)'
  };
  position: relative;
  backdrop-filter: blur(15px);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateX(6px) translateY(-2px) scale(1.02);
    box-shadow: ${props => 
      props.$isCurrentPlayer
        ? '0 12px 35px rgba(102, 126, 234, 0.5), inset 0 1px 0 rgba(255,255,255,0.3)'
        : props.$isLeader
          ? '0 10px 30px rgba(255, 215, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
          : '0 8px 25px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
    };

    &::before {
      left: 100%;
    }
  }

  .player-info {
    display: flex;
    align-items: center;
    flex: 1;
    z-index: 1;
  }

  .player-name {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  }

  .status-badges {
    display: flex;
    gap: 6px;
    align-items: center;
    z-index: 1;
  }
`;

const StatusBadge = styled.div<{ $type: 'dealer' | 'turn' }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  background: ${props => 
    props.$type === 'dealer'
      ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 50%, #e74c3c 100%)'
      : 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 50%, #2ecc71 100%)'
  };
  color: white;
  box-shadow: ${props => 
    props.$type === 'dealer'
      ? '0 3px 10px rgba(255, 107, 107, 0.5), inset 0 1px 0 rgba(255,255,255,0.3)'
      : '0 3px 10px rgba(78, 205, 196, 0.5), inset 0 1px 0 rgba(255,255,255,0.3)'
  };
  border: 2px solid rgba(255,255,255,0.4);
  position: relative;
  backdrop-filter: blur(10px);
  animation: ${props => props.$type === 'turn' ? 'pulse 2s infinite' : 'none'};
  
  &::before {
    content: '${props => props.$type === 'dealer' ? 'D' : 'T'}';
    position: relative;
    z-index: 2;
  }

  &::after {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    background: ${props => 
      props.$type === 'dealer'
        ? 'linear-gradient(45deg, #ff6b6b, #ee5a52, #ff6b6b)'
        : 'linear-gradient(45deg, #4ecdc4, #44a08d, #4ecdc4)'
    };
    border-radius: 50%;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::after {
    opacity: 0.4;
    animation: rotate 2s linear infinite;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Update center area to better fit smaller cards
const CenterArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  height: 60vh;
`;

const TurnIndicator = styled.div`
  padding: 12px 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%);
  border-radius: 20px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  box-shadow: 
    0 8px 20px rgba(102, 126, 234, 0.4),
    inset 0 1px 0 rgba(255,255,255,0.2);
  text-align: center;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.8px;
  backdrop-filter: blur(15px);
  border: 2px solid rgba(255,255,255,0.15);
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, 
      #667eea 0%, #764ba2 50%, #667eea 100%);
    border-radius: 22px;
    z-index: -1;
    animation: borderFlow 3s linear infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 20%;
    left: -100%;
    width: 100%;
    height: 60%;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255,255,255,0.15) 50%, 
      transparent 100%);
    animation: slideShine 2s ease-in-out infinite;
  }
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 
      0 12px 25px rgba(102, 126, 234, 0.5),
      inset 0 1px 0 rgba(255,255,255,0.3);
  }
  
  @keyframes borderFlow {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes slideShine {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

// Update the cards area styles
const CardsArea = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
  padding: 10px;
  min-height: ${198}px;
  position: relative;
`;

const DeckArea = styled.div`
  display: flex;
  gap: 280px;
  align-items: center;
  justify-content: center;
  min-height: ${198}px;
  position: relative;
  top: 50%;
  transform: translateY(-50%);
`;

const DeckStack = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StackedCard = styled.div<{
  $stackIndex: number;
}>`
  position: absolute;
  transform: translate(
    ${props => props.$stackIndex * 12}px,
    ${props => props.$stackIndex * -0.5}px
  );
  z-index: ${props => props.$stackIndex};
`;

const MixedStackCard = styled.div<{
  $stackIndex: number;
  $isAlternate: boolean;
}>`
  position: absolute;
  transform: translate(
    ${props => props.$stackIndex * 6 + (props.$isAlternate ? 25 : 0)}px,
    ${props => props.$stackIndex * -3}px
  );
  z-index: ${props => props.$stackIndex};
`;

const DiscardCard = styled.div`
  position: relative;
  z-index: 100;
`;

const InfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 10px;
`;

const InfoIcon = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
  cursor: help;
  transition: background 0.2s;

  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;

const Tooltip = styled.div`
  position: relative;
  
  &:hover::after {
    content: attr(title);
    position: absolute;
    left: 40px;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0,0,0,0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    white-space: nowrap;
    z-index: 1000;
  }
`;

const PlayerHandArea = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: ${198 + 40}px;
`;

const PlayerName = styled.div`
  text-align: center;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 15px;
  color: white;
`;

const CardsContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
  max-width: 100%;
  align-items: center;
  min-height: ${198}px;
  position: relative;
  perspective: 1000px;
  transform-style: preserve-3d;
`;

const CardWrapper = styled.div<{ 
  $faceUp: boolean; 
  $isDragging?: boolean;
  $dragPosition?: { x: number; y: number };
  $isMoving?: boolean;
  $movePosition?: number;
}>`
  margin-right: ${props => props.$faceUp ? '10px' : '-80px'};
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  user-select: none;
  position: relative;
  transform: ${props => {
    if (props.$isDragging && props.$dragPosition) {
      return `translate3d(${props.$dragPosition.x}px, ${props.$dragPosition.y}px, 0)`;
    }
    if (props.$isMoving && typeof props.$movePosition === 'number') {
      return `translate3d(${props.$movePosition}px, 0, 0)`;
    }
    return 'translate3d(0, 0, 0)';
  }};
  opacity: ${props => props.$isDragging ? 0.8 : 1};
  transition: ${props => props.$isMoving ? 'transform 0.3s ease' : 'none'};
  z-index: ${props => props.$isDragging ? 1000 : 1};
`;

const ButtonGroup = styled.div`
  position: fixed;
  bottom: 300px;
  right: 80px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  z-index: 1000;
`;

const EndPhaseButton = styled.button`
  padding: 16px 32px;
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 50%, #27ae60 100%);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 
    0 8px 20px rgba(39, 174, 96, 0.4),
    inset 0 2px 0 rgba(255,255,255,0.2),
    inset 0 -2px 0 rgba(0,0,0,0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 
      0 12px 30px rgba(39, 174, 96, 0.5),
      inset 0 2px 0 rgba(255,255,255,0.3),
      inset 0 -2px 0 rgba(0,0,0,0.2);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 
      0 6px 15px rgba(39, 174, 96, 0.4),
      inset 0 2px 0 rgba(255,255,255,0.2),
      inset 0 -2px 0 rgba(0,0,0,0.1);
  }
`;

const NextGameButton = styled.button`
  padding: 16px 32px;
  background: linear-gradient(135deg, #3498db 0%, #667eea 50%, #3498db 100%);
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 
    0 8px 20px rgba(52, 152, 219, 0.4),
    inset 0 2px 0 rgba(255,255,255,0.2),
    inset 0 -2px 0 rgba(0,0,0,0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 
      0 12px 30px rgba(52, 152, 219, 0.5),
      inset 0 2px 0 rgba(255,255,255,0.3),
      inset 0 -2px 0 rgba(0,0,0,0.2);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 
      0 6px 15px rgba(52, 152, 219, 0.4),
      inset 0 2px 0 rgba(255,255,255,0.2),
      inset 0 -2px 0 rgba(0,0,0,0.1);
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255,255,255,0.1);
  border-left-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(135deg, #2ecc71, #27ae60);
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  color: white;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  padding: 20px;
  text-align: center;
`;

export default GameScreen; 